import * as React from 'react'
import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import ProfileMenu from './ProfileMenu'
import './style.css'
import { useUserContext } from 'common/context/UserContext'
import { useAppContext } from 'common/context/AppContext'
import { CommonRoutes, getBaseRoute, getRouteActionTitle, getRouteTitleAlias, normalizeRoutePath } from 'common/context/commonRoutes.ts'
import { Button, Spin, Tooltip } from 'antd'
import { PlusOutlined } from '@ant-design/icons'
import { AdvertiserBalanceBean, NewAdvertiserPaymentBean } from 'common/api/api'
import { balanceApi } from 'common/api/entitiesApi'
import { AddFundsModal } from 'common/components/AddFundsModal'
import { formatBalance, showError, showMessage } from 'common/util/Util'
import { menuTabs } from '@/components/sidebar/sidebarTabsAdvertiser.tsx'

interface BreadcrumbProps {
    collapsed;
    balance?: AdvertiserBalanceBean;
    onBalanceRefresh?: () => void;
    stripePaymentsEnabled: boolean;
}

export const BreadcrumbAdvertiser: React.FC<BreadcrumbProps> = ({collapsed, balance, onBalanceRefresh, stripePaymentsEnabled}) => {
    const [breadcrumb, setBreadcrumb] = useState<any>([]);
    const [addFundsModalOpen, setAddFundsModalOpen] = useState(false)
    const [paymentLoading, setPaymentLoading] = useState(false)

    const location = useLocation();
    const {blackTheme, user} = useUserContext();
    const appContext = useAppContext();
    const {preloader, contextHolder} = appContext;
    const menuTitleByKey = React.useMemo(() => {
        const map = new Map<string, string>();
        menuTabs.forEach((item) => {
            if (item?.url && item?.label) {
                map.set(normalizeRoutePath(item.url), item.label);
            }
        });
        return map;
    }, []);

    const getBreadcrumb = () => {
        let pathParts = location.pathname.split('/').filter(Boolean);
        const sectionRoute = getBaseRoute(location.pathname);

        const crumbs: React.ReactNode[] = [];

        crumbs.push(
            <Link key="home" className="breadcrumbBar__item" to="/">
                Home
            </Link>
        );

        if (pathParts.length > 0) {
            const sectionName = getItemName(sectionRoute);
            crumbs.push(<span className="breadcrumbBar__arrow" key="arrow1"/>);
            crumbs.push(
                <Link
                    key="section"
                    className="breadcrumbBar__item"
                    to={sectionRoute === CommonRoutes.PROFILE ? location.pathname : sectionRoute}
                >
                    {sectionName}
                </Link>
            );
        }

        if (pathParts.some((p) => ['create', 'edit', 'copy'].includes(p))) {
            crumbs.push(<span className="breadcrumbBar__arrow" key="arrow2"/>);
            const action = pathParts.find((p) =>
                ['create', 'edit', 'copy'].includes(p)
            );
            const actionLabel =
                action === 'create'
                    ? 'New'
                    : action === 'edit'
                        ? 'Edit'
                        : action === 'copy'
                            ? 'Copy'
                            : '';
            crumbs.push(
                <span key="action" className="breadcrumbBar__item">
          {actionLabel} {getRouteActionTitle(sectionRoute, getItemName(sectionRoute))}
        </span>
            );
        }

        return crumbs;
    };

    const getItemName = (pathname: string) => {
        return getRouteTitleAlias(pathname) || menuTitleByKey.get(pathname) || 'Dashboard';
    };

    useEffect(() => {
        setBreadcrumb(getBreadcrumb());
    }, [location]);

    const closeAddFundsModal = () => {
        setAddFundsModalOpen(false)
    }

    const startPayment = (amount: number) => {
        if (!user?.advertiserId) return

        // Opening the tab synchronously avoids browser popup blocking after the API request completes.
        const paymentWindow = window.open('', '_blank')
        if (!paymentWindow) {
            showMessage(appContext.messageApi, 'Error', 'Allow popups to continue to Stripe.')
            return
        }

        setPaymentLoading(true)
        balanceApi.startAdvertiserPayment({
            advertiserId: user.advertiserId,
            amount,
            paySystem: NewAdvertiserPaymentBean.PaySystemEnum.STRIPE,
        }).then((res) => {
            if (!res.data) throw new Error('Payment URL is missing')
            paymentWindow.location.href = res.data
            const interval = window.setInterval(() => {
                if (paymentWindow.closed) {
                    window.clearInterval(interval)
                    onBalanceRefresh?.()
                }
            }, 1000)
            closeAddFundsModal()
        }).catch((error) => {
            paymentWindow.close()
            showError(appContext, error)
        }).finally(() => setPaymentLoading(false))
    }

    return (
        <div
            className={`breadcrumbBar ${blackTheme ? 'breadcrumb_black' : 'breadcrumb_white'}`}
            style={{
                width: `calc(100% - ${collapsed ? 80 : 214}px)`
            }}
        >
            <div style={{display: 'flex', alignItems: 'center'}}>
                <div
                    className={`breadcrumbBar__path ${blackTheme ? 'breadcrumbBar__path_black' : 'breadcrumbBar__path_white'}`}
                    style={{display: 'inline-block'}}>
                    {breadcrumb}
                </div>
                {preloader && (
                    <div
                        style={{
                            marginLeft: '10px',
                            minWidth: '120px',
                            minHeight: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            flexShrink: 0
                        }}
                    >
                        <Spin tip="Loading" size="large"/>
                    </div>
                )}
            </div>

            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 60
            }}>
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '36px',
                        minWidth: '146px',
                        backgroundColor: '#FFFFFF1F',
                        borderRadius: '4px',
                        color: '#FFFFFF'
                    }}
                >
                    {!balance ? (
                        <Spin/>
                    ) : (
                        <div
                            style={{
                                color: '#FFFFFF',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '36px',
                            }}
                        >
                            Balance:&nbsp;<span
                            style={{fontWeight: 600}}>{'$' + formatBalance(balance.runtimeAvailable)}</span>
                        </div>
                    )}
                </div>
                <Tooltip title={stripePaymentsEnabled ? undefined : 'Stripe payments are currently unavailable'}>
                    <span>
                        <Button
                            icon={<PlusOutlined />}
                            style={{marginLeft: '10px', color: '#FFFFFF', backgroundColor: '#FFFFFF1F', height: '36px'}}
                            type="text"
                            disabled={!stripePaymentsEnabled}
                            onClick={() => setAddFundsModalOpen(true)}
                        >
                            Add Funds
                        </Button>
                    </span>
                </Tooltip>
                <ProfileMenu/>
            </div>

            <AddFundsModal
                open={addFundsModalOpen}
                loading={paymentLoading}
                onCancel={closeAddFundsModal}
                onSubmit={startPayment}
            />
            {contextHolder}
        </div>
    );
};
